import { useState } from "react";
import { toast } from "sonner";
import { CarFront, Warehouse, Flag, Hotel, Globe2, HandCoins, CalendarClock, ShieldCheck, ArrowDown, Loader2, CheckCircle2, Paperclip, X } from "lucide-react";
import { VerifiedPartners } from "@/components/site/PartnerCards";
import { useLang } from "@/context/LangContext";
import { http, formatErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const HERO = "https://images.pexels.com/photos/39229797/pexels-photo-39229797.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const TYPES = [["driver", CarFront], ["fleet", Warehouse], ["guide", Flag], ["acc", Hotel]];
const BENEFITS = [["pt_b1", Globe2], ["pt_b2", HandCoins], ["pt_b3", CalendarClock], ["pt_b4", ShieldCheck]];
export const PT_CATS = ["driver", "fleet", "guide", "accommodation"];
export const PT_CITIES = ["padang", "bukittinggi", "mentawai", "other"];
const toForm = () => document.getElementById(window.innerWidth < 1024 ? "partner-apply" : "partner-form")?.scrollIntoView({ behavior: "smooth" });

function Hero() {
  const { t } = useLang();
  return (
    <section className="mx-auto max-w-7xl px-4 pt-8 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-[2rem] bg-[#022C22]">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#022C22] via-[#022C22]/80 to-transparent" />
        <div className="relative max-w-2xl px-6 py-16 sm:px-12 sm:py-24">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-amber-400 fade-up">{t("pt_title")}</p>
          <h1 data-testid="partner-hero-title" className="font-display text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-6xl fade-up">{t("pt_hero_h")}</h1>
          <p className="mt-5 max-w-xl text-base text-emerald-50/85 md:text-lg fade-up" style={{ animationDelay: "80ms" }}>{t("pt_hero_sub")}</p>
          <button onClick={toForm} data-testid="partner-hero-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#E11D48] px-7 py-3.5 font-bold text-white shadow-lg shadow-rose-900/30 transition-transform hover:-translate-y-0.5 fade-up" style={{ animationDelay: "160ms" }}>
            {t("pt_cta")}<ArrowDown className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

function Types() {
  const { t } = useLang();
  return (
    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
      <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-[#D97706]">{t("pt_kicker")}</p>
      <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{t("pt_types_title")}</h2>
      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {TYPES.map(([k, Icon], i) => (
          <div key={k} data-testid={`partner-type-${k}`} className="group rounded-3xl border border-slate-200 bg-white p-6 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl fade-up" style={{ animationDelay: `${i * 70}ms` }}>
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-[#064E3B] transition-colors group-hover:bg-[#064E3B] group-hover:text-white"><Icon className="h-6 w-6" /></span>
            <h3 className="mt-5 text-lg font-bold text-slate-900">{t(`pt_${k}`)}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{t(`pt_${k}_d`)}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Benefits() {
  const { t } = useLang();
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">{t("pt_why")}</h2>
      <ul className="space-y-4">{BENEFITS.map(([k, Icon]) => (
        <li key={k} data-testid={`partner-benefit-${k}`} className="flex gap-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-400/15 text-amber-400"><Icon className="h-5 w-5" /></span>
          <p className="pt-2 text-sm leading-relaxed text-emerald-50/90 md:text-base">{t(k)}</p>
        </li>
      ))}</ul>
    </div>
  );
}

const Select = ({ testid, value, onChange, opts }) => (
  <select data-testid={testid} required value={value} onChange={onChange} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-emerald-500">
    {opts.map(([v, l]) => <option key={v} value={v} disabled={!v}>{l}</option>)}
  </select>
);

const DOC_OK = ["image/jpeg", "image/png", "image/webp", "application/pdf"];

function DocPicker({ files, setFiles }) {
  const { t } = useLang();
  const add = (e) => {
    const picked = [...e.target.files].filter((x) => DOC_OK.includes(x.type) && x.size <= 5 * 1024 * 1024);
    if (picked.length < e.target.files.length || files.length + picked.length > 3) toast.error(t("pt_docs_hint"));
    setFiles([...files, ...picked].slice(0, 3));
    e.target.value = "";
  };
  return (
    <div data-testid="partner-docs" className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4">
      <Label>{t("pt_docs")}</Label>
      <p className="mt-1 text-xs text-slate-500">{t("pt_docs_hint")}</p>
      <ul className="mt-2 space-y-1">{files.map((x, i) => (
        <li key={`${x.name}-${i}`} data-testid={`partner-doc-item-${i}`} className="flex items-center gap-2 text-xs text-slate-700"><Paperclip className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{x.name}</span>
          <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} data-testid={`partner-doc-remove-${i}`} className="ml-auto rounded-full p-0.5 text-slate-400 hover:bg-slate-200"><X className="h-3.5 w-3.5" /></button></li>
      ))}</ul>
      {files.length < 3 && (
        <label className="mt-2 inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 transition-colors hover:bg-slate-100">
          <Paperclip className="h-3.5 w-3.5" />{t("pt_docs_add")}
          <input type="file" multiple accept=".jpg,.jpeg,.png,.webp,.pdf" onChange={add} data-testid="partner-docs-input" className="hidden" />
        </label>
      )}
    </div>
  );
}

function ApplyForm() {
  const { t, lang } = useLang();
  const empty = { name: "", phone: "", category: "", city: "", details: "" };
  const [f, setF] = useState(empty);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post("/partner-applications", { ...f, lang });
      let failed = false;
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await http.post(`/partner-applications/${data.id}/documents?token=${data.upload_token}`, fd).catch(() => { failed = true; });
      }
      if (failed) toast.warning(t("pt_docs_failed"), { duration: 9000 });
      toast.success(t("pt_success"), { duration: 8000 });
      setDone(true);
      setF(empty);
      setFiles([]);
    } catch (err) { toast.error(formatErr(err)); } finally { setBusy(false); }
  };
  if (done) return (
    <div data-testid="partner-success" className="flex flex-col items-center justify-center gap-3 rounded-3xl bg-white p-8 text-center">
      <CheckCircle2 className="h-14 w-14 text-emerald-600" />
      <p className="font-semibold text-slate-800">{t("pt_success")}</p>
    </div>
  );
  return (
    <form id="partner-apply" onSubmit={submit} data-testid="partner-form-el" className="scroll-mt-24 space-y-4 rounded-3xl bg-white p-6 shadow-2xl sm:p-8">
      <h2 className="text-xl font-bold text-slate-900">{t("pt_form_title")}</h2>
      <div><Label>{t("pt_name")}</Label><Input data-testid="partner-name-input" required minLength={2} maxLength={120} value={f.name} onChange={set("name")} /></div>
      <div><Label>{t("pt_phone")}</Label><Input data-testid="partner-phone-input" type="tel" inputMode="numeric" pattern="\+?[0-9 \-]{8,20}" required placeholder="08xxxxxxxxxx" value={f.phone}
        onInvalid={(e) => e.target.setCustomValidity(t("pt_phone_invalid"))} onChange={(e) => { e.target.setCustomValidity(e.target.value.replace(/\D/g, "").length < 8 && e.target.value ? t("pt_phone_invalid") : ""); set("phone")(e); }} /></div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div><Label>{t("pt_category")}</Label><Select testid="partner-category-select" value={f.category} onChange={set("category")} opts={[["", t("pt_select")], ...PT_CATS.map((c) => [c, t(`pt_cat_${c}`)])]} /></div>
        <div><Label>{t("pt_city")}</Label><Select testid="partner-city-select" value={f.city} onChange={set("city")} opts={[["", t("pt_select")], ...PT_CITIES.map((c) => [c, t(`city_${c}`)])]} /></div>
      </div>
      <div><Label>{t("pt_details")}</Label><Textarea data-testid="partner-details-input" required minLength={5} maxLength={1000} rows={4} placeholder={t("pt_details_ph")} value={f.details} onChange={set("details")} /></div>
      <DocPicker files={files} setFiles={setFiles} />
      <button disabled={busy} data-testid="partner-submit-btn" className="flex w-full items-center justify-center gap-2 rounded-full bg-[#E11D48] py-3.5 font-bold text-white transition-colors hover:bg-[#BE123C] disabled:opacity-60">
        {busy && <Loader2 className="h-5 w-5 animate-spin" />}{busy && files.length ? t("pt_docs_uploading") : t("pt_submit")}
      </button>
    </form>
  );
}

export default function Partner() {
  return (
    <div data-testid="partner-page">
      <Hero />
      <Types />
      <VerifiedPartners />
      <section id="partner-form" className="mx-auto max-w-7xl scroll-mt-24 px-4 sm:px-6 lg:px-8">
        <div className="grid items-start gap-10 rounded-[2rem] bg-[#064E3B] p-6 sm:p-12 lg:grid-cols-2">
          <Benefits />
          <ApplyForm />
        </div>
      </section>
    </div>
  );
}
